import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send, Mic, Image, Smile, Video,
  ChevronLeft, MoreVertical, Edit2, Check, Bot, UserPlus
} from 'lucide-react';
import api from '../api';
import { NoAutoFillInput } from './ui/NoAutoFillInput';

const UserGroupChat = ({ group, onClose, userId, userName, userAvatar, onUpdateGroupName, onAddFriend }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [newGroupName, setNewGroupName] = useState(group.name);
  const [isLoading, setIsLoading] = useState(false);
  const [aiAutoReply, setAiAutoReply] = useState(false);
  const [processedMessageIds, setProcessedMessageIds] = useState(new Set());
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // AI Chat Modal states
  const [showAiChatModal, setShowAiChatModal] = useState(false);
  const [aiChatTarget, setAiChatTarget] = useState(null);
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatInput, setAiChatInput] = useState('');
  const [aiChatRound, setAiChatRound] = useState(0);
  const [aiChatShowAddFriend, setAiChatShowAddFriend] = useState(false);
  const [aiChatHasRequested, setAiChatHasRequested] = useState(false);
  const aiChatMaxRounds = 10;

  const emojis = ['😀', '😂', '🥰', '😎', '🤔', '👍', '❤️', '🔥', '🎉', '👏', '🤖', '👻', '👽', '🤡', '💀', '👾'];

  const loadMessages = useCallback(async () => {
    try {
      setIsLoading(true);
      const msgs = await api.getGroupMessages(group.id, 100);
      setMessages(msgs);
    } catch (err) {
      console.error('加载消息失败:', err);
    } finally {
      setIsLoading(false);
    }
  }, [group.id]);

  useEffect(() => {
    loadMessages();
    const interval = setInterval(loadMessages, 3000);

    if (api.socket && group?.type === 'ai_group') {
      api.socket.emit('join-chat', `group_${group.id}`);

      const handleNewMessage = (data) => {
        if (data.groupId === group.id) {
          setMessages(prev => {
            const exists = prev.find(m => m.id === data.id);
            if (exists) return prev;
            return [...prev, data];
          });
        }
      };

      api.socket.on('new_group_message', handleNewMessage);

      return () => {
        clearInterval(interval);
        api.socket.off('new_group_message', handleNewMessage);
      };
    }

    return () => clearInterval(interval);
  }, [group.id, loadMessages, group?.type]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!aiAutoReply || messages.length === 0) return;

    const handleMentionReply = async () => {
      for (const msg of messages) {
        if (msg.senderId === userId) continue;
        if (processedMessageIds.has(msg.id)) continue;
        if (msg.senderName?.includes('AI')) continue;

        const mentionPattern = new RegExp(`@${userId}|@${userName}`, 'i');
        const isMentioned = mentionPattern.test(msg.content);

        if (isMentioned) {
          setProcessedMessageIds(prev => new Set([...prev, msg.id]));

          try {
            const aiResponse = await api.getAIReply(userId, msg.senderId, msg.content);
            const aiReply = typeof aiResponse === 'string' ? aiResponse : aiResponse.reply;
            const avatarName = typeof aiResponse === 'object' && aiResponse.avatarName ? aiResponse.avatarName : `${userName}`;
            const aiMsg = await api.sendGroupMessage(
              group.id,
              userId,
              avatarName,
              userAvatar || '',
              aiReply,
              'text'
            );
            aiMsg.isAI = true;
            setMessages(prev => [...prev, aiMsg]);
          } catch (err) {
            console.error('AI群聊回复失败:', err);
          }
        }
      }
    };

    handleMentionReply();
  }, [messages, aiAutoReply, userId, userName, userAvatar, group.id, processedMessageIds]);

  const handleSend = async () => {
    if (!inputValue.trim()) return;
    
    try {
      const msg = await api.sendGroupMessage(
        group.id, 
        userId, 
        userName, 
        userAvatar || '',
        inputValue.trim(), 
        'text'
      );
      setMessages(prev => [...prev, msg]);
      setInputValue('');
    } catch (err) {
      console.error('发送消息失败:', err);
      alert('发送失败: ' + err.message);
    }
  };

  const handleEmojiSelect = (emoji) => {
    setInputValue(prev => prev + emoji);
    setShowEmoji(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const msg = await api.sendGroupMessage(
          group.id, userId, userName, userAvatar || '',
          '图片', 'image', reader.result
        );
        setMessages(prev => [...prev, msg]);
      } catch (err) {
        console.error('发送图片失败:', err);
        alert('发送图片失败: ' + err.message);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 100 * 1024 * 1024) {
      alert('视频大小不能超过100MB（约360秒）');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const msg = await api.sendGroupMessage(
          group.id, userId, userName, userAvatar || '',
          '视频', 'video', reader.result
        );
        setMessages(prev => [...prev, msg]);
      } catch (err) {
        console.error('发送视频失败:', err);
        alert('发送视频失败: ' + err.message);
      }
    };
    reader.readAsDataURL(file);
  };

  const toggleRecording = () => {
    setIsRecording(!isRecording);
    if (!isRecording) {
      setTimeout(() => {
        setIsRecording(false);
        alert('语音消息已录制（模拟）');
      }, 2000);
    }
  };

  const handleUpdateGroupName = async () => {
    if (!newGroupName.trim() || newGroupName === group.name) {
      setIsEditingName(false);
      setNewGroupName(group.name);
      return;
    }
    
    try {
      if (onUpdateGroupName) {
        onUpdateGroupName(group.id, newGroupName.trim());
      }
      setIsEditingName(false);
    } catch (err) {
      console.error('更新群聊名称失败:', err);
      alert('更新失败: ' + err.message);
      setNewGroupName(group.name);
      setIsEditingName(false);
    }
  };

  // AI Chat Modal Functions
  const handleAvatarClick = (member) => {
    if (member.userId === userId) return;
    
    setAiChatTarget(member);
    setAiChatMessages([
      {
        id: 1,
        sender: 'ai',
        text: `你好，我是${member.senderName || member.userName || '用户'}的数字分身。我们可以进行最多${aiChatMaxRounds}轮对话。`,
        timestamp: Date.now()
      }
    ]);
    setAiChatRound(0);
    setAiChatShowAddFriend(false);
    setAiChatHasRequested(false);
    setShowAiChatModal(true);
  };

  const handleAiChatSend = async () => {
    if (!aiChatInput.trim() || aiChatRound >= aiChatMaxRounds) return;

    const userMsg = {
      id: Date.now(),
      sender: 'me',
      text: aiChatInput.trim(),
      timestamp: Date.now()
    };
    setAiChatMessages(prev => [...prev, userMsg]);
    setAiChatInput('');

    const newRound = aiChatRound + 1;
    setAiChatRound(newRound);

    try {
      const aiResponse = await api.getAIReply(aiChatTarget.userId, 'stranger', userMsg.text, aiChatTarget.senderName || aiChatTarget.userName);
      const aiReplyText = typeof aiResponse === 'string' ? aiResponse : aiResponse.reply;
      
      const aiMsg = {
        id: Date.now() + 1,
        sender: 'ai',
        text: aiReplyText,
        timestamp: Date.now()
      };
      setAiChatMessages(prev => [...prev, aiMsg]);

      if (newRound >= aiChatMaxRounds) {
        setTimeout(() => {
          setAiChatShowAddFriend(true);
          const limitMsg = {
            id: Date.now() + 2,
            sender: 'system',
            text: `对话轮数已达上限（${aiChatMaxRounds}/${aiChatMaxRounds}）。如需继续对话，请添加对方为好友。`,
            timestamp: Date.now()
          };
          setAiChatMessages(prev => [...prev, limitMsg]);
        }, 500);
      }
    } catch (error) {
      console.error('获取AI回复失败:', error);
      const errorMsg = {
        id: Date.now() + 1,
        sender: 'system',
        text: 'AI回复失败，请稍后再试。',
        timestamp: Date.now()
      };
      setAiChatMessages(prev => [...prev, errorMsg]);
    }
  };

  const handleAiChatAddFriend = () => {
    setAiChatHasRequested(true);
    if (onAddFriend) {
      onAddFriend(aiChatTarget.userId, aiChatTarget.senderName || aiChatTarget.userName);
    }
    
    const event = new CustomEvent('strangerChatEndedWithoutFriendRequest', {
      detail: {
        targetUserId: aiChatTarget.userId,
        targetUserName: aiChatTarget.senderName || aiChatTarget.userName,
        chatLog: aiChatMessages.filter(m => m.sender !== 'system'),
        roundCount: aiChatRound,
        fromUserId: userId,
        fromUserName: userName
      }
    });
    window.dispatchEvent(event);

    const requestMsg = {
      id: Date.now(),
      sender: 'system',
      text: '已发送好友申请，等待对方确认...',
      timestamp: Date.now()
    };
    setAiChatMessages(prev => [...prev, requestMsg]);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const isOwner = group.ownerId === userId;

  // Get unique members from messages for avatar display
  const uniqueMembers = React.useMemo(() => {
    const members = new Map();
    messages.forEach(msg => {
      if (msg.senderId && msg.senderId !== userId && !members.has(msg.senderId)) {
        members.set(msg.senderId, {
          userId: msg.senderId,
          senderName: msg.senderName,
          senderAvatar: msg.senderAvatar
        });
      }
    });
    return Array.from(members.values());
  }, [messages, userId]);

  return (
    <div className="fixed inset-0 z-50 bg-[#050510] flex flex-col">
      <div className="h-28 flex items-end justify-between px-6 pb-4 glass-card rounded-b-[40px] z-20">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10">
            <ChevronLeft size={24} className="text-white" />
          </button>
          <div className="flex-1">
            {isEditingName && isOwner ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  className="bg-white/10 border border-white/20 rounded-lg px-3 py-1 text-white text-sm w-48"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  autoFocus
                  onBlur={handleUpdateGroupName}
                  onKeyPress={(e) => e.key === 'Enter' && handleUpdateGroupName()}
                />
                <button onClick={handleUpdateGroupName} className="p-1 bg-[var(--neon-primary)] rounded">
                  <Check size={16} className="text-black" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-white">{group.name}</h2>
                {isOwner && (
                  <button 
                    onClick={() => setIsEditingName(true)}
                    className="p-1 hover:bg-white/10 rounded"
                  >
                    <Edit2 size={14} className="text-gray-400" />
                  </button>
                )}
              </div>
            )}
            <p className="text-xs text-gray-400">{group.members?.length || 0} 成员</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAiAutoReply(!aiAutoReply)}
            className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium transition-colors ${
              aiAutoReply
                ? 'bg-purple-500 text-white'
                : 'bg-white/10 text-white'
            }`}
          >
            <Bot size={16} />
            {aiAutoReply ? 'AI开' : 'AI关'}
          </button>
          <button className="p-2 rounded-full hover:bg-white/10">
            <MoreVertical size={24} className="text-white" />
          </button>
        </div>
      </div>

      {/* Active Members Avatars */}
      {uniqueMembers.length > 0 && (
        <div className="px-4 py-3 border-b border-white/5 overflow-x-auto">
          <div className="flex gap-3">
            {uniqueMembers.map(member => (
              <button
                key={member.userId}
                onClick={() => handleAvatarClick(member)}
                className="flex flex-col items-center min-w-[60px] group"
              >
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-transparent group-hover:border-[var(--neon-primary)] transition-all">
                  <img 
                    src={member.senderAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${member.userId}`} 
                    alt={member.senderName}
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-[10px] text-gray-400 mt-1 truncate max-w-[60px]">
                  {member.senderName}
                </span>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-gray-500 mt-2 text-center">点击头像可与对方AI分身交流</p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-40">
        {isLoading && messages.length === 0 && (
          <div className="text-center text-gray-500 py-10">
            <p>加载中...</p>
          </div>
        )}
        
        {messages.length === 0 && !isLoading && (
          <div className="text-center text-gray-500 py-10">
            <p>还没有消息，开始聊天吧！</p>
          </div>
        )}
        
        {messages.map((msg) => {
          const isMe = msg.senderId === userId;
          const isAI = msg.isAI || msg.senderName?.includes('AI');
          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] ${isMe ? 'items-end' : 'items-start'} flex flex-col`}>
                {!isMe && (
                  <span className={`text-[10px] mb-1 ml-1 ${isAI ? 'text-purple-400' : 'text-gray-500'}`}>
                    {msg.senderName}
                  </span>
                )}
                <div className={`rounded-2xl px-4 py-3 ${
                  isMe
                    ? 'bg-[var(--neon-primary)] text-black rounded-br-none'
                    : isAI
                      ? 'bg-purple-500/80 text-white rounded-bl-none border border-purple-400/50'
                      : 'glass-card text-white rounded-bl-none'
                }`}>
                  {msg.type === 'text' && <p className="text-sm">{msg.content}</p>}
                  {msg.type === 'image' && (
                    <img src={msg.mediaUrl || msg.senderAvatar} alt="图片" className="max-w-full rounded-lg" />
                  )}
                  {msg.type === 'video' && (
                    <video src={msg.mediaUrl} controls className="max-w-full rounded-lg" />
                  )}
                  {msg.type === 'voice' && (
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-8 bg-black/20 rounded-full flex items-center px-3">
                        <div className="flex gap-0.5">
                          {[1,2,3,4].map(i => (
                            <div key={i} className="w-1 bg-current rounded-full animate-pulse" style={{height: `${Math.random() * 16 + 4}px`}} />
                          ))}
                        </div>
                      </div>
                      <span className="text-xs">{msg.content}</span>
                    </div>
                  )}
                </div>
                <span className={`text-[10px] text-gray-500 mt-1 ${isMe ? 'mr-1' : 'ml-1'}`}>
                  {formatTime(msg.createdAt)}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {showEmoji && (
        <div className="fixed bottom-24 left-4 right-4 bg-[#1a1a2e] rounded-2xl p-4 z-50 border border-white/10">
          <div className="grid grid-cols-8 gap-2">
            {emojis.map(emoji => (
              <button
                key={emoji}
                onClick={() => handleEmojiSelect(emoji)}
                className="text-2xl p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="fixed bottom-0 left-0 w-full px-4 pb-6 pt-4 bg-gradient-to-t from-[#050510] via-[#050510]/95 to-transparent safe-bottom">
        <div className="glass-card rounded-[32px] p-2 flex items-center gap-2">
          <input
            type="file"
            accept="image/*"
            className="hidden"
            ref={fileInputRef}
            onChange={handleImageUpload}
          />
          <input
            type="file"
            accept="video/*"
            className="hidden"
            ref={videoInputRef}
            onChange={handleVideoUpload}
          />
          
          <button 
            onClick={() => fileInputRef.current?.click()}
            className="w-10 h-10 rounded-full flex items-center justify-center text-gray-400 hover:text-[var(--neon-primary)] hover:bg-white/10"
          >
            <Image size={20} />
          </button>
          
          <button 
            onClick={() => videoInputRef.current?.click()}
            className="w-10 h-10 rounded-full flex items-center justify-center text-gray-400 hover:text-[var(--neon-primary)] hover:bg-white/10"
          >
            <Video size={20} />
          </button>
          
          <button 
            onClick={() => setShowEmoji(!showEmoji)}
            className={`w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 ${showEmoji ? 'text-[var(--neon-primary)]' : 'text-gray-400'}`}
          >
            <Smile size={20} />
          </button>
          
          <NoAutoFillInput
            type="text"
            inputMode="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="输入消息..."
            name="group-message"
            id="group-message"
            className="flex-1 bg-transparent border-none outline-none text-white py-2"
          />
          
          <button 
            onClick={toggleRecording}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              isRecording ? 'bg-red-500 text-white animate-pulse' : 'text-gray-400 hover:text-[var(--neon-primary)] hover:bg-white/10'
            }`}
          >
            <Mic size={20} />
          </button>
          
          <button
            onClick={handleSend}
            disabled={!inputValue.trim()}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              inputValue.trim() 
                ? 'bg-[var(--neon-primary)] text-black' 
                : 'bg-white/10 text-gray-500'
            }`}
          >
            <Send size={18} />
          </button>
        </div>
        
        {isRecording && (
          <div className="text-center mt-2 text-red-400 text-sm animate-pulse">
            正在录音...
          </div>
        )}
      </div>

      {/* AI Chat Modal */}
      {showAiChatModal && aiChatTarget && (
        <div className="absolute inset-0 z-[100] bg-[#050510] flex flex-col page-enter">
          <div className="h-20 flex items-center justify-between px-6 glass-card border-b border-white/5">
            <div className="flex items-center gap-3">
              <button onClick={() => setShowAiChatModal(false)} className="p-2 hover:bg-white/10 rounded-full">
                <ChevronLeft size={24} className="text-white" />
              </button>
              <div className="flex items-center gap-3">
                <img 
                  src={aiChatTarget.senderAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${aiChatTarget.userId}`} 
                  alt={aiChatTarget.senderName} 
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--neon-primary)] to-blue-600" 
                />
                <div>
                  <h3 className="text-white font-bold">{aiChatTarget.senderName}</h3>
                  <p className="text-xs text-[var(--neon-primary)]">
                    {aiChatRound < aiChatMaxRounds ? `剩余${aiChatMaxRounds - aiChatRound}轮对话` : '对话已结束'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-40">
            {aiChatMessages.map(msg => (
              <div key={msg.id} className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`}>
                {msg.sender === 'ai' && (
                  <img 
                    src={aiChatTarget.senderAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${aiChatTarget.userId}`} 
                    alt="AI" 
                    className="w-8 h-8 rounded-full mr-2 bg-gradient-to-br from-[var(--neon-primary)] to-blue-600" 
                  />
                )}
                <div className={`max-w-[75%] px-4 py-3 rounded-2xl ${
                  msg.sender === 'me' 
                    ? 'bg-[var(--neon-primary)] text-black rounded-br-none' 
                    : msg.sender === 'system'
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    : 'glass-card text-white rounded-bl-none'
                }`}>
                  <p className="text-sm">{msg.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="absolute bottom-0 left-0 w-full px-4 pb-6 pt-4 bg-gradient-to-t from-[#050510] via-[#050510]/95 to-transparent safe-bottom">
            {aiChatShowAddFriend ? (
              <div className="space-y-3">
                {!aiChatHasRequested ? (
                  <button
                    onClick={handleAiChatAddFriend}
                    className="w-full py-4 bg-[var(--neon-primary)] text-black rounded-2xl font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                  >
                    <UserPlus size={20} />
                    添加对方为好友
                  </button>
                ) : (
                  <div className="w-full py-4 bg-white/10 text-white rounded-2xl font-bold text-center">
                    已发送好友申请
                  </div>
                )}
                <button
                  onClick={() => setShowAiChatModal(false)}
                  className="w-full py-3 bg-white/5 text-gray-400 rounded-2xl font-medium hover:bg-white/10 transition-colors"
                >
                  结束对话
                </button>
              </div>
            ) : (
              <div className="glass-card rounded-[32px] p-2 flex items-center gap-2">
                <input
                  type="text"
                  value={aiChatInput}
                  onChange={(e) => setAiChatInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAiChatSend()}
                  placeholder={aiChatRound < aiChatMaxRounds ? "输入消息..." : "对话已结束"}
                  disabled={aiChatRound >= aiChatMaxRounds}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-form-type="other"
                  data-lpignore="true"
                  className="flex-1 bg-transparent border-none outline-none text-white py-3 px-4 disabled:opacity-50"
                />
                <button
                  onClick={handleAiChatSend}
                  disabled={!aiChatInput.trim() || aiChatRound >= aiChatMaxRounds}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                    aiChatInput.trim() && aiChatRound < aiChatMaxRounds
                      ? 'bg-[var(--neon-primary)] text-black'
                      : 'bg-white/10 text-gray-500'
                  }`}
                >
                  <Send size={20} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserGroupChat;
